import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, notifyRefresh } from "@/lib/api";

const EMPTY = {
  name: "", cpf: "", rg: "", birth_date: "", gender: "", profession: "", phone: "", email: "", address: "",
  emergency_contact: "", notes: "",
  ortho: { active: false, monthly_fee: 0, due_day: 10, start_date: "" },
  return_plan: { active: false, interval_months: 6, next_date: "" },
};

const pick = (p) => ({
  ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p?.[k] ?? EMPTY[k]])),
  ortho: { ...EMPTY.ortho, ...(p?.ortho || {}) }, return_plan: { ...EMPTY.return_plan, ...(p?.return_plan || {}) },
});

const Field = ({ label, children, className = "" }) => (
  <div className={`grid gap-1.5 ${className}`}><Label>{label}</Label>{children}</div>
);

export const PatientFormDialog = ({ open, onOpenChange, patient, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState(pick());
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setF(pick(patient)); }, [open, patient]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setO = (k, v) => setF({ ...f, ortho: { ...f.ortho, [k]: v } });
  const setR = (k, v) => setF({ ...f, return_plan: { ...f.return_plan, [k]: v } });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, ortho: { ...f.ortho, monthly_fee: Number(f.ortho.monthly_fee) || 0, due_day: Number(f.ortho.due_day) || 10 },
      return_plan: { ...f.return_plan, interval_months: Number(f.return_plan.interval_months) || 6 } };
    try {
      const r = patient ? await api.put(`/patients/${patient.id}`, body) : await api.post("/patients", body);
      toast.success(patient ? "Cadastro atualizado" : "Paciente cadastrado");
      notifyRefresh();
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="patient-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{patient ? "Editar paciente" : "Cadastrar paciente"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Nome completo *" className="sm:col-span-2">
              <Input value={f.name} onChange={set("name")} required minLength={2} data-testid="patient-name-input" />
            </Field>
            <Field label="Telefone / WhatsApp"><Input value={f.phone} onChange={set("phone")} placeholder="(11) 99999-9999" data-testid="patient-phone-input" /></Field>
            <Field label="E-mail"><Input type="email" value={f.email} onChange={set("email")} data-testid="patient-email-input" /></Field>
            <Field label="CPF"><Input value={f.cpf} onChange={set("cpf")} data-testid="patient-cpf-input" /></Field>
            <Field label="RG"><Input value={f.rg} onChange={set("rg")} data-testid="patient-rg-input" /></Field>
            <Field label="Data de nascimento"><Input type="date" value={f.birth_date} onChange={set("birth_date")} data-testid="patient-birth-input" /></Field>
            <Field label="Sexo">
              <Select value={f.gender || "na"} onValueChange={(v) => setF({ ...f, gender: v === "na" ? "" : v })}>
                <SelectTrigger data-testid="patient-gender-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="na">Não informado</SelectItem>
                  <SelectItem value="F">Feminino</SelectItem>
                  <SelectItem value="M">Masculino</SelectItem>
                  <SelectItem value="O">Outro</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Profissão"><Input value={f.profession} onChange={set("profession")} data-testid="patient-profession-input" /></Field>
            <Field label="Contato de emergência"><Input value={f.emergency_contact} onChange={set("emergency_contact")} data-testid="patient-emergency-input" /></Field>
            <Field label="Endereço" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="patient-address-input" /></Field>
            <Field label="Observações" className="sm:col-span-2"><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="patient-notes-input" /></Field>
          </div>

          <div className="rounded-2xl border border-cyan-100 bg-cyan-50/40 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-800">Tratamento com aparelho (mensalidade)</p>
                <p className="text-xs text-slate-500">Gera a mensalidade automaticamente todo mês</p>
              </div>
              <Switch checked={f.ortho.active} onCheckedChange={(v) => setO("active", v)} data-testid="patient-ortho-switch" />
            </div>
            {f.ortho.active && (
              <div className="mt-3 grid grid-cols-3 gap-3">
                <Field label="Valor (R$)"><Input type="number" step="0.01" min="0" value={f.ortho.monthly_fee} onChange={(e) => setO("monthly_fee", e.target.value)} data-testid="patient-ortho-fee-input" /></Field>
                <Field label="Dia do vencimento"><Input type="number" min="1" max="31" value={f.ortho.due_day} onChange={(e) => setO("due_day", e.target.value)} data-testid="patient-ortho-day-input" /></Field>
                <Field label="Início"><Input type="date" value={f.ortho.start_date} onChange={(e) => setO("start_date", e.target.value)} data-testid="patient-ortho-start-input" /></Field>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-sky-100 bg-sky-50/40 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-800">Consultas de retorno</p>
                <p className="text-xs text-slate-500">Lembrete de revisão periódica</p>
              </div>
              <Switch checked={f.return_plan.active} onCheckedChange={(v) => setR("active", v)} data-testid="patient-return-switch" />
            </div>
            {f.return_plan.active && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Intervalo">
                  <Select value={String(f.return_plan.interval_months)} onValueChange={(v) => setR("interval_months", v)}>
                    <SelectTrigger data-testid="patient-return-interval-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 6, 12].map((m) => <SelectItem key={m} value={String(m)}>A cada {m} {m === 1 ? "mês" : "meses"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Próximo retorno"><Input type="date" value={f.return_plan.next_date} onChange={(e) => setR("next_date", e.target.value)} data-testid="patient-return-date-input" /></Field>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="patient-save-btn">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
